import { LOCAL_SPACE_X, LOCAL_SPACE_Y } from '@library/constants'
import type { ICanvasSize, MapHeader, Point2D, Range2D, ScaleType } from '@library/types'
import { glMatrix, mat3, vec2 } from 'gl-matrix'

const getPixelRatio = (canvas: ICanvasSize): Point2D => ({
  x: canvas.width / canvas.offsetWidth,
  y: canvas.height / canvas.offsetHeight,
})

export const getCoordsToCanvasMatrix = (
  sourceMat: mat3,
  canvas: ICanvasSize,
  zoom: ScaleType,
): mat3 => {
  const pixelRatio = getPixelRatio(canvas)
  const pixelRatioMat = mat3.scale(mat3.create(), sourceMat, [pixelRatio.x, pixelRatio.y])
  const translateMat = mat3.translate(mat3.create(), pixelRatioMat, [zoom.x, zoom.y])
  const scaleMat = mat3.scale(mat3.create(), translateMat, [zoom.scaleX, zoom.scaleY])
  return scaleMat
}

export const getProjectionMatrix = (canvas: ICanvasSize, zoom: ScaleType): mat3 => {
  const projectionMat = mat3.projection(mat3.create(), canvas.width, canvas.height)
  return getCoordsToCanvasMatrix(projectionMat, canvas, zoom)
}

export const getGlobalToLocalMatrix = (sourceMat: mat3, origin: Range2D): mat3 => {
  const aspectRatio = origin.height / origin.width
  const scaleMat = mat3.scale(mat3.create(), sourceMat, [
    LOCAL_SPACE_X / origin.width,
    (LOCAL_SPACE_Y / origin.height) * aspectRatio,
  ])
  const invertYMat = mat3.scale(mat3.create(), scaleMat, [1, -1])
  const offsetMat = mat3.translate(mat3.create(), invertYMat, [-origin.x, -origin.y])
  return offsetMat
}

export const getGlobalToCanvasMatrix = (
  canvas: ICanvasSize,
  zoom: ScaleType,
  origin: Range2D,
): mat3 => {
  const localMat = getCoordsToCanvasMatrix(mat3.create(), canvas, zoom)
  const globalMat = getGlobalToLocalMatrix(localMat, origin)
  return globalMat
}

export const getCanvasToGlobalMatrix = (
  canvas: ICanvasSize,
  zoom: ScaleType,
  origin: Range2D,
): mat3 => {
  const mat = getGlobalToCanvasMatrix(canvas, zoom, origin)
  return mat3.invert(mat3.create(), mat)
}

/**
 * Вычисляет матрицу преобразования координат карты в экранные координаты (пространство отсечения)
 * @param canvas область, в которой выполняется поиск проецирование точки
 * @param zoom параметры масштабирования на {@link canvas}'е
 * @param rotate угол вращения в радианах для карты
 * @param rotationPoint точка, относительно которой выполняется вращение карты
 * @returns матрица преобразования координат
 */
export const getGridCoordinatesToProjectionMatrix = (
  canvas: ICanvasSize,
  zoom: ScaleType,
  rotate: number,
  rotationPoint: Point2D,
): mat3 => {
  const projectionMatrix = getProjectionMatrix(canvas, zoom)
  const offsetMat = mat3.translate(mat3.create(), projectionMatrix, [
    rotationPoint.x,
    rotationPoint.y,
  ])
  const rotateMat = mat3.rotate(mat3.create(), offsetMat, rotate)
  return rotateMat
}

/**
 * Вычисляет матрицу преобразования координат карты в глобальные координаты
 * @param rotationPoint точка вращения карты
 * @param rotationAngle угол вращения карты в радианах
 * @returns матрица преобразования координат
 */
export const getGridCoordinatesToGlobalMatrix = (
  sourceMatrix: mat3,
  rotationPoint: Point2D,
  rotationAngle: number,
): mat3 => {
  const offsetMat = mat3.translate(mat3.create(), sourceMatrix, [rotationPoint.x, rotationPoint.y])
  const rotateMat = mat3.rotate(mat3.create(), offsetMat, -rotationAngle)
  return rotateMat
}

/**
 * Вычисляет матрицу преобразования индексов карты в глобальные координаты
 * @param mapHeader параметры карты
 * @returns матрица преобразования координат
 */
export const getGridIndexToGlobalMatrix = (mapHeader: MapHeader): mat3 => {
  const gridMat = getGridCoordinatesToGlobalMatrix(
    mat3.create(),
    { x: mapHeader.offsetX, y: mapHeader.offsetY },
    glMatrix.toRadian(mapHeader.rotationAngle),
  )
  const scaleMat = mat3.scale(
    mat3.create(),
    gridMat,
    vec2.fromValues(mapHeader.stepX, mapHeader.stepY),
  )
  return scaleMat
}

/**
 * Вычисляет матрицу преобразования индексов карты в глобальные координаты
 * @param mapHeader параметры карты
 * @returns матрица преобразования координат
 */
export const getGlobalToGridIndexMatrix = (mapHeader: MapHeader): mat3 => {
  const gridMat = getGridIndexToGlobalMatrix(mapHeader)
  const invertMat = mat3.invert(mat3.create(), gridMat)
  return invertMat
}

export const applyMatToVec2 = (mat: mat3, point: Point2D): Point2D => {
  const geoPoint = vec2.transformMat3(vec2.create(), vec2.fromValues(point.x, point.y), mat)
  return {
    x: geoPoint[0],
    y: geoPoint[1],
  }
}
